import { HubConnectionBuilder, HubConnectionState, LogLevel, HttpError } from '@microsoft/signalr';
import { applicationStore } from '../../store/applicationStore';
import { GameLiveConnectionMethod } from './types';
import { userInfoStore } from '../../store/userInfoStore';
import { iframeService } from '../iframeService';
import { isPG } from '../../core/helpers/skinHelper';
import {
  reportStateStatuses,
  ResponseTypeForGamesByRound,
  ValidationResponseCodeForGameByRound,
  ValidationType,
} from '../../core/constants/validationType';
import { DefaultQueryParams } from '../../core/constants/defaultQueryParams';
import { parseQuery } from '../../core/helpers/parseQuery';
import {
  AllGamesConfigurations,
  GameIds,
  ROUND_GAMES_GAME_IDS,
} from '../../core/constants/gameIds';
import { notificationStore } from '../../store/notificationStore';
import { liveUpdateStore } from '../../store/liveUpdateStore';
import { THIS_GAMES_COLUMN_ID } from '../../core/constants/liveBetsContants';
import { userSettingsStore } from '../../store/userSettingsStore';
import { checkHashStore } from '../../store/checkHashStore';
import { autoBetStore } from '../../store/autoBetStore';
import { parseNumberByPrecision } from '../../core/helpers/parseNumberByPrecision';
import { partnerSkinEnums } from '../../core/constants/partnerSkinEnums';
import { freeBetStore } from '../../store/freeBetStore';

export class BaseLiveConnection {
  connection;

  connectionStartPromise;

  connectionStopPromise;

  url;

  isOnReconnectingState;

  constructor(url) {
    this.url = url;
  }

  async waitStarted() {
    if (this.connectionStartPromise instanceof Promise) {
      await this.connectionStartPromise;
      this.connectionStartPromise = null;
    }
  }

  async waitStop() {
    if (this.connectionStopPromise instanceof Promise) {
      await this.connectionStopPromise;
      this.connectionStopPromise = null;
    }
  }

  async init(reconnectTimes = [300, 600]) {
    await this.waitStop();
    await this.waitStarted();
    if (this.connection?.state !== HubConnectionState.Connected) {
      this.connection = this.createConnection(reconnectTimes);
      this.connectionStartPromise = this.connection.start();
      await this.waitStarted();
    }
    return this.connection;
  }

  createConnection(reconnectTimes = [300, 600]) {
    return new HubConnectionBuilder()
      .withUrl(this.url, {
        accessTokenFactory: () => {
          const { accessToken } = applicationStore;
          return accessToken;
        },
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect(reconnectTimes)
      .build();
  }

  async invoke(methodName, ...args) {
    await this.waitStarted();
    if (this.connection.state === HubConnectionState.Connected) {
      try {
        this.connection.invoke(methodName, ...args);
      } catch (err) {
        this.stop();
        applicationStore.setIsConnectionLost(true);
      }
    }
    return null;
  }

  on(methodName, handler) {
    if (this.connection.state === HubConnectionState.Connected)
      this.connection.on(methodName, handler);
    return this;
  }

  onReconnecting(callback) {
    if (this.connection) {
      this.connection.onreconnecting((data) => {
        if (callback) {
          callback(data);
        }
        this.isOnReconnectingState = true;
        this.callbackMiddleware({
          methodName: GameLiveConnectionMethod.Reconnecting,
        });
      });
    }
    return null;
  }

  onClose(callback) {
    if (this.connection && this.connection.state === HubConnectionState.Connected) {
      return this.connection.onclose((error) => {
        // eslint-disable-next-line no-console
        console.error('[BaseLiveConnection] onclose(): event onclose socket, error =', error);

        if (callback) {
          // eslint-disable-next-line no-console
          console.log('[BaseLiveConnection] onClose(): called callback, error =', error);
          callback(error);
        }

        if (this.isOnReconnectingState) {
          // eslint-disable-next-line no-console
          console.warn(
            '[BaseLiveConnection] onClose(): isOnReconnectingState=true, call callbackMiddleware for method ConnectionClose'
          );
          this.callbackMiddleware({
            methodName: GameLiveConnectionMethod.ConnectionClose,
          });
        }

        // eslint-disable-next-line no-console
        console.log(
          '[BaseLiveConnection] onClose(): after closing, connection.state =',
          this.connection.state
        );
      });
    }
    // eslint-disable-next-line no-console
    console.warn(
      '[BaseLiveConnection] onClose(): try to register onclose, ' +
        'but connection not existing or not in connected state, state =',
      this.connection?.state
    );
    return null;
  }

  onReconnected(callback) {
    if (this.connection) {
      return this.connection.onreconnected((data) => {
        if (callback) {
          callback(data);
        }

        this.isOnReconnectingState = false;
        this.callbackMiddleware({
          methodName: GameLiveConnectionMethod.Reconnected,
        });
      });
    }
    return null;
  }

  async stop() {
    await this.waitStarted();
    await this.waitStop();
    if (this.connection?.state === HubConnectionState.Connected) {
      this.connectionStopPromise = this.connection.stop().then(() => {
        this.connectionStartPromise = null;
      });
      await this.waitStop();
    }
  }

  callbackMiddleware({ data, methodName, callback }) {
    const methods = {
      [GameLiveConnectionMethod.GetInitialStateResult]: ({
        rate,
        hash,
        limits,
        defaultBetAmount,
        balance,
        quickBets,
        precision,
        responseCode,
        currencyCode,
        userFreebets,
        displayCurrency,
        displayQuickBets,
        displayCurrencyRate,
      }) => {
        const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
        autoBetStore.resetAutoBetState();
        userInfoStore.setUserConfigs({
          rate,
          limits,
          balance,
          quickBets,
          precision,
          displayCurrencyRate,
        });
        if (callback) {
          callback(data);
        }
        userInfoStore.setDefaultBetAmount(defaultBetAmount !== 0 ? defaultBetAmount : quickBets[0]);
        if (AllGamesConfigurations[gameId].isRoundGame) {
          userInfoStore.setMaxWinLimit(data.limits.maxWin);
          userInfoStore.setIsAllowedChangeCurrency(false);
          userInfoStore.setLimits(limits);
        }
        if (AllGamesConfigurations[gameId].isGameInChips) {
          userInfoStore.setQuickBets(
            quickBets,
            AllGamesConfigurations[gameId].chipColors,
            displayQuickBets
          );
          userInfoStore.setLimits({
            minBet: displayCurrency ? displayQuickBets[0] : quickBets[0],
            maxBet: displayCurrency
              ? displayQuickBets[displayQuickBets.length - 1]
              : quickBets[quickBets.length - 1],
          });
          const { currentFreeBet } = freeBetStore;
          const selectedQuickBet =
            userInfoStore.quickBets.find((item) => item.value === currentFreeBet?.amount) ||
            (data.spinInitialInfo?.spinsInfo
              ? userInfoStore.quickBets?.find(
                  (item) => item.value === data.spinInitialInfo.spinsInfo[0].mainSpin.amount
                )
              : userInfoStore.quickBets?.find(
                  (item) => item.value === userInfoStore.defaultBetAmount
                ));
          userInfoStore.setSelectedChip(selectedQuickBet?.chip);
          userInfoStore.setBetAmount(
            displayCurrency ? selectedQuickBet?.displayValue : selectedQuickBet?.value
          );
          userInfoStore.setMaxWinLimit(data.maxWinLimit);
        } else if (!AllGamesConfigurations[gameId].isRoundGame) {
          userInfoStore.setMaxWinLimit(data?.maxWinLimit);
          userInfoStore.setLimits({
            minBet: quickBets[0],
            maxBet: quickBets[quickBets.length - 1],
          });
          if (data?.spinInitialInfo) {
            userInfoStore.setBetAmount(
              parseNumberByPrecision(
                data.spinInitialInfo.spinsInfo
                  ? data.spinInitialInfo.spinsInfo[0].mainSpin.amount
                  : userInfoStore.defaultBetAmount,
                1,
                userInfoStore.precision
              )
            );
          } else if (AllGamesConfigurations[gameId]?.isSpecialSpinInfo) {
            userInfoStore.setBetAmount(
              parseNumberByPrecision(
                data.additionalInfo
                  ? data.additionalInfo.currentState.status === 1
                    ? data.additionalInfo.currentState.amount
                    : data?.lastSpinInfo
                    ? data.lastSpinInfo.amount
                    : userInfoStore.defaultBetAmount
                  : userInfoStore.defaultBetAmount,
                // : userInfoStore.defaultBetAmount,
                1,
                userInfoStore.precision
              )
            );
            userInfoStore.setLimits({
              minBet: quickBets[0],
              maxBet: quickBets[quickBets.length - 1],
            });
          } else {
            userInfoStore.setBetAmount(
              parseNumberByPrecision(
                data.spinInfo ? data.spinInfo.amount : userInfoStore.defaultBetAmount,
                1,
                userInfoStore.precision
              )
            );
          }
        }

        if (userFreebets && userFreebets.length) {
          freeBetStore.showFreeBetInfo(userFreebets);
        }
        if (!AllGamesConfigurations[gameId].isRoundGame) {
          checkHashStore.setNextRoundHash(hash);
        }
        userSettingsStore.setIsShowMusicIcon(!!AllGamesConfigurations[gameId].isShowMusicIcon);
        userSettingsStore.setIsShowTurboIcon(!!AllGamesConfigurations[gameId].isShowTurboIcon);
        userSettingsStore.setIsShowHotKeys(!!AllGamesConfigurations[gameId].isShowHotKeys);
        applicationStore.setIsCardsGame(!!AllGamesConfigurations[gameId].isCardsGame);
        userInfoStore.setCurrencyCodeAbbr(currencyCode);
        userInfoStore.setDisplayCurrency(displayCurrency);
        applicationStore.setIsSocketConnected(true);
        applicationStore.setIsConnectionLost(false);
        applicationStore.setIsReconnect(false);

        const inValidTokenResponseCode = ROUND_GAMES_GAME_IDS.includes(+gameId)
          ? ValidationResponseCodeForGameByRound[ResponseTypeForGamesByRound.InValidToken]
          : ValidationType.InvalidToken;

        if (!isPG() && responseCode === inValidTokenResponseCode) {
          this.stop();
          iframeService.sendLoginCheck();
          applicationStore.setAccessToken('');
          applicationStore.setIsConnectionLost(true);
        }
        if (displayQuickBets) {
          userInfoStore.setDisplayQuickBets(displayQuickBets);
        }
        if (userInfoStore.currencyFromToken !== (displayCurrency || currencyCode)) {
          userInfoStore.setNeedChangeCurrency(true);
        }
        if (isPG()) {
          liveUpdateStore.setSelectedColumn(THIS_GAMES_COLUMN_ID);
        }
        if (applicationStore.partnerSkin !== partnerSkinEnums.pg) {
          applicationStore.setIsOverwriteMainCss(true);
        }
      },
      [GameLiveConnectionMethod.DoBetResult]: (arg) => {
        const { [DefaultQueryParams.GameId]: gameId, [DefaultQueryParams.UserId]: userId } =
          parseQuery(window.location.search);
        if (
          !isPG() &&
          arg.responseCode ===
            (ROUND_GAMES_GAME_IDS.includes(+gameId)
              ? ValidationResponseCodeForGameByRound[ResponseTypeForGamesByRound.InValidToken]
              : ValidationType.InvalidToken)
        ) {
          this.stop();
          iframeService.sendLoginCheck();
          applicationStore.setAccessToken('');
          applicationStore.setIsConnectionLost(true);
        }
        if (
          data.userId === Number(userId) &&
          arg.responseCode ===
            (ROUND_GAMES_GAME_IDS.includes(+gameId)
              ? ValidationResponseCodeForGameByRound[ResponseTypeForGamesByRound.Success]
              : ValidationType.Success)
        ) {
          if (isPG()) {
            notificationStore.setNotification(arg.responseCode);
          }
        }
        if (arg.operatorError) {
          notificationStore.setOperatorError(arg.operatorError);
        }
      },
      [GameLiveConnectionMethod.DoSpinResult]: (arg) => {
        const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
        if (
          !isPG() &&
          arg.responseCode ===
            (ROUND_GAMES_GAME_IDS.includes(+gameId)
              ? ValidationResponseCodeForGameByRound[ResponseTypeForGamesByRound.InValidToken]
              : ValidationType.InvalidToken)
        ) {
          this.stop();
          iframeService.sendLoginCheck();
          applicationStore.setAccessToken('');
          applicationStore.setIsConnectionLost(true);
        }
        if (arg.freebetInfo) {
          freeBetStore.updateCurrentFreeBet(arg.freebetInfo);
        }
        if (arg.responseCode === ValidationType.Success) {
          if (isPG()) {
            notificationStore.setNotification(arg.responseCode);
          }
          const amount = userInfoStore.betAmount;
          if (+gameId !== +GameIds.Fishing) {
            iframeService.sendRoundStarted(arg.balance);
          }
          if (
            +gameId !== +GameIds.BlackJack &&
            +gameId !== +GameIds.CoinFlip &&
            +gameId !== +GameIds.Drift &&
            +gameId !== +GameIds.Football &&
            +gameId !== +GameIds.Hilo &&
            +gameId !== +GameIds.Mines
          ) {
            setTimeout(() => {
              iframeService.sendRoundFinished(
                arg?.totalWinAmount || arg.winAmount,
                userInfoStore.totalBetAmount || amount
              );
            }, 2000);
          }
        }
        if (arg.operatorError) {
          notificationStore.setOperatorError(arg.operatorError);
        }
      },
      [GameLiveConnectionMethod.ReportStateResult]: (arg) => {
        if (arg.status === reportStateStatuses.win || arg.status === reportStateStatuses.lose) {
          iframeService.sendRoundFinished(arg.winAmount, arg.amount);
        }
      },
      [GameLiveConnectionMethod.BalanceUpdate]: (arg) => userInfoStore.setUserBalance(arg),
      [GameLiveConnectionMethod.GameOpen]: () => {
        if (this.connection?.connectionId !== data.connectionId) {
          this.stop();
          if (!isPG()) {
            iframeService.sendLoginCheck();
          }
          applicationStore.setAccessToken('');
          applicationStore.setIsConnectionLost(true);
        }
      },
      [GameLiveConnectionMethod.ConnectionClose]: () => {
        this.stop();
        applicationStore.setIsConnectionLost(true);
      },
      [GameLiveConnectionMethod.Error]: () => {
        if (data instanceof HttpError && data.status === 401) {
          this.stop();
          applicationStore.setIsConnectionLost(true);
          iframeService.sendPgLogin();
        }
      },
      [GameLiveConnectionMethod.Reconnecting]: () => {},
      [GameLiveConnectionMethod.Reconnected]: () => {
        applicationStore.setIsReconnect(false);
      },
      [GameLiveConnectionMethod.RoundStarted]: () => {
        iframeService.sendRoundStarted(userInfoStore.userBalance);
      },
      [GameLiveConnectionMethod.RoundFinished]: ({ data: arg }) => {
        const { [DefaultQueryParams.UserId]: userId } = parseQuery(window.location.search);
        const userBets = arg.bets.find((item) => +userId === +item.userId);
        iframeService.sendRoundFinished(userBets?.winAmount || 0, userBets?.amount || 0);
      },
      [GameLiveConnectionMethod.CashOutResult]: ({ winAmount, responseCode }) => {
        if (responseCode === ValidationType.Success) {
          iframeService.sendRoundFinished(winAmount || 0, userInfoStore.betAmount || 0);
        }
      },
    };
    methods[methodName]?.(data);
  }
}
